import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { syncPolarCustomerToProfile } from '@/api/polar';

// User Profile Type
export interface UserProfile {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  subscription_tier: 'free' | 'weekly' | 'solo' | 'pro' | 'pro_byok';
  subscription_status: 'active' | 'cancelled' | 'expired' | 'trialing' | 'onboarding';
  subscription_end_date: string | null;
  polar_customer_id: string | null;
  polar_subscription_id: string | null;
  is_founders_club: boolean;
  onboarding_completed: boolean;
  onboarding_document_id: string | null;
  onboarding_reward_claimed: boolean;
  onboarding_steps: {
    created_brand: boolean;
    visited_document: boolean;
    used_tone_fit: boolean;
    used_rewrite: boolean;
    used_fact_check: boolean;
  };
  preferences: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

// Feature Gating Result
export interface FeatureGatingResult {
  isLimitedTier: boolean;
  maxWritingStyles: number;
  canCreateWritingStyle: (currentCount: number) => boolean;
  showWritingStyleUpgrade: (currentCount: number) => void;
  maxDiscoveryFeeds: number;
  canCreateDiscoveryFeed: (currentCount: number) => boolean;
  showDiscoveryFeedUpgrade: (currentCount: number) => void;
  tierName: string;
  isUnlimited: boolean;
}

// Context Type
interface UserContextType {
  // Auth
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUp: (email: string, password: string) => Promise<{ data: any; error: any }>;

  // Profile
  profile: UserProfile | null;
  profileError: Error | null;
  refreshProfile: () => Promise<void>;
  isOnboarding: boolean;
  isSubscribed: boolean;
  subscriptionTier: UserProfile['subscription_tier'] | undefined;
  subscriptionStatus: UserProfile['subscription_status'] | undefined;

  // Feature Gating
  featureGating: FeatureGatingResult;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { toast } = useToast();

  // Get auth state from AuthContext (single source of truth)
  const { user, loading: authLoading } = useAuth();

  // Profile loading state (separate from auth loading)
  const [profileLoading, setProfileLoading] = useState(true);

  // Profile State
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileError, setProfileError] = useState<Error | null>(null);

  // Auth Methods
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  };

  // Fetch Profile
  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('[UserContext] Error fetching profile:', error);
        setProfileError(new Error(error.message));
        return;
      }

      if (data) {
        setProfile(data as UserProfile);
        setProfileError(null);
      }
    } catch (err) {
      console.error('[UserContext] Error fetching profile:', err);
      setProfileError(err instanceof Error ? err : new Error('Failed to fetch profile'));
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    
    // Sync subscription data from Polar before fetching profile
    // This ensures we have the latest subscription status
    try {
      await syncPolarCustomerToProfile(user.id);
    } catch (error) {
      console.error('[UserContext] Error syncing Polar data:', error);
      // Continue with profile fetch even if sync fails
    }
    
    await fetchProfile(user.id);
  };

  // Fetch profile when user ID changes (from AuthContext)
  // Use user.id instead of user object to avoid refetching on every user object change
  useEffect(() => {
    const loadProfile = async () => {
      if (user?.id) {
        setProfileLoading(true);
        
        // Sync subscription data from Polar on initial load
        try {
          await syncPolarCustomerToProfile(user.id);
        } catch (error) {
          console.error('[UserContext] Error syncing Polar data on load:', error);
          // Continue with profile fetch even if sync fails
        }
        
        await fetchProfile(user.id);
        setProfileLoading(false);
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [user?.id]); // Only depends on user ID, not the entire user object

  // Feature Gating Logic (all features available on all plans)
  const isLimitedTier = false;
  const isUnlimited = true;

  const maxWritingStyles = Infinity;
  const maxDiscoveryFeeds = Infinity;

  const getTierName = (): string => {
    if (profile?.subscription_status === 'onboarding') return 'Onboarding';
    if (profile?.subscription_status === 'trialing') return 'Trial';
    if (profile?.subscription_tier === 'free') return 'Free';
    if (profile?.subscription_tier === 'weekly') return 'Weekly';
    if (profile?.subscription_tier === 'solo') return 'Solo';
    if (profile?.subscription_tier === 'pro') return 'Pro';
    if (profile?.subscription_tier === 'pro_byok') return 'Pro BYOK';
    return 'Free';
  };

  const canCreateWritingStyle = useCallback((currentCount: number): boolean => {
    return currentCount < maxWritingStyles;
  }, [maxWritingStyles]);

  const showWritingStyleUpgrade = useCallback((currentCount: number): void => {
    if (currentCount < maxWritingStyles) return;

    toast({
      title: "Upgrade Required",
      description: `You've reached the limit of ${maxWritingStyles} writing style${maxWritingStyles === 1 ? '' : 's'} on your ${getTierName()} plan. Upgrade to create unlimited writing styles.`,
      variant: "destructive",
    });
  }, [maxWritingStyles, toast]);

  const canCreateDiscoveryFeed = useCallback((currentCount: number): boolean => {
    return currentCount < maxDiscoveryFeeds;
  }, [maxDiscoveryFeeds]);

  const showDiscoveryFeedUpgrade = useCallback((currentCount: number): void => {
    if (currentCount < maxDiscoveryFeeds) return;

    toast({
      title: "Upgrade Required",
      description: `You've reached the limit of ${maxDiscoveryFeeds} custom feed${maxDiscoveryFeeds === 1 ? '' : 's'} on your ${getTierName()} plan. Upgrade to create unlimited feeds.`,
      variant: "destructive",
    });
  }, [maxDiscoveryFeeds, toast]);

  const featureGating: FeatureGatingResult = useMemo(() => ({
    isLimitedTier,
    maxWritingStyles,
    canCreateWritingStyle,
    showWritingStyleUpgrade,
    maxDiscoveryFeeds,
    canCreateDiscoveryFeed,
    showDiscoveryFeedUpgrade,
    tierName: getTierName(),
    isUnlimited,
  }), [isLimitedTier, maxWritingStyles, canCreateWritingStyle, showWritingStyleUpgrade, maxDiscoveryFeeds, canCreateDiscoveryFeed, showDiscoveryFeedUpgrade, isUnlimited]);

  const value: UserContextType = useMemo(() => ({
    // Auth (from AuthContext)
    user,
    loading: authLoading || profileLoading, // Combined loading state
    signOut,
    signIn,
    signUp,

    // Profile
    profile,
    profileError,
    refreshProfile,
    isOnboarding: profile?.subscription_status === 'onboarding',
    isSubscribed: profile?.subscription_status === 'active',
    subscriptionTier: profile?.subscription_tier,
    subscriptionStatus: profile?.subscription_status,

    // Feature Gating
    featureGating,
  }), [user, authLoading, profileLoading, signOut, signIn, signUp, profile, profileError, refreshProfile, featureGating]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Hook to use User Context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Backward compatibility hook (deprecated - use useAuth from AuthContext instead)
export const useUserAuth = () => {
  const { user, loading, signOut, signIn, signUp } = useUser();
  return { user, loading, signOut, signIn, signUp };
};

export const useProfile = () => {
  const { profile, loading, profileError, refreshProfile, isOnboarding, isSubscribed, subscriptionTier, subscriptionStatus } = useUser();
  return {
    profile,
    loading,
    error: profileError,
    refreshProfile,
    isOnboarding,
    isSubscribed,
    subscriptionTier,
    subscriptionStatus
  };
};

export const useFeatureGating = () => {
  const { featureGating } = useUser();
  return featureGating;
};
